import React, { useEffect, useMemo, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'

// 자금계획 / 계획 대비 실적 — 같은 표를 세 가지로 바꿔 본다.
//
// 계획은 사람이 읽는 항목(매출입금·인건비·임차료…)으로 월별로 세운다. 실적은 따로 입력하지
// 않는다 — 이미 들어와 있는 은행 거래에서 그 달 현금 입출금을 모아 계정과목으로 항목에
// 붙인다. 계좌끼리 옮긴 돈은 수입도 지출도 아니라 뺀다.
//
// 계정과목이 아직 안 붙은 거래는 "미지정" 줄로 그대로 드러낸다. 숨기면 항목 합계와 실제
// 통장 증감이 안 맞는데, 그 차이를 모른 채 계획을 세우는 게 제일 위험하다.

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
const MODES = [
  { k: 'all', label: '한눈에' },
  { k: 'plan', label: '계획' },
  { k: 'actual', label: '실적' },
  { k: 'diff', label: '차이' },
  { k: 'map', label: '분류 규칙' },
]
const KINDS = ['거래처', '부서', '계좌', '계정과목']
const 억 = (v) => (v ? (v / 1e8).toFixed(1) : '')

export default function FundPlan({ defaultMode = 'all', currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [year, setYear] = useState(new Date().getFullYear())
  const [mode, setMode] = useState(defaultMode)
  const [unit, setUnit] = useState('억')
  const [data, setData] = useState(null)
  const [edits, setEdits] = useState({})
  const [drafts, setDrafts] = useState({})   // 입력 중인 글자 그대로
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState('')
  const [maps, setMaps] = useState([])
  const [draft, setDraft] = useState({ 종류: '거래처', 키: '', item_id: '' })
  // 종류마다 고를 대상이 다르다. 빈 칸에 조각을 적으라고만 하면 뭘 적어야 할지 알 수 없다.
  const [depts, setDepts] = useState([])
  const [accts, setAccts] = useState([])
  const [cats, setCats] = useState([])
  const [vsug, setVsug] = useState([])
  const [detail, setDetail] = useState(null)   // {side, itemId, itemName, month}

  // 셀을 누르면 그 안의 거래를 그대로 연다. 미지정이 왜 안 줄어드는지는 그 안을 봐야 알고,
  // 그걸 봐야 규칙을 걸 수 있다.
  const openDetail = (side, id, m, name) => {
    if (mode === 'plan') return
    setDetail({ side, itemId: id, month: m, name, loading: true })
    const q = `year=${year}&side=${encodeURIComponent(side)}&month=${m || 0}`
      + (id ? `&item=${id}` : '')
    portal.get(`/api/fund-plan/detail?${q}`)
      .then((r) => setDetail((p) => (p ? { ...p, ...r, loading: false } : p)))
      .catch((e) => setDetail((p) => (p ? { ...p, err: e.message, loading: false } : p)))
  }

  useEffect(() => {
    if (mode !== 'map') return
    portal.get('/api/fa/meta/departments').then(setDepts).catch(() => {})
    portal.get('/api/fa/meta/categories').then(setCats).catch(() => {})
    portal.get('/api/accounts/settings')
      .then((r) => setAccts((r.accounts || []).filter((a) => !a.excluded))).catch(() => {})
  }, [mode])

  useEffect(() => {
    if (draft.종류 !== '거래처' || draft.키.trim().length < 1) { setVsug([]); return }
    const t = setTimeout(() => {
      portal.get(`/api/vendors/search?q=${encodeURIComponent(draft.키.trim())}&kind=general`)
        .then(setVsug).catch(() => setVsug([]))
    }, 250)
    return () => clearTimeout(t)
  }, [draft.종류, draft.키])

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2500) }

  const load = () => {
    setErr(''); setData(null); setEdits({})
    portal.get(`/api/fund-plan?year=${year}&actual=1`)
      .then(setData)
      .catch((e) => setErr(e.status === 403
        ? '자금계획 화면 권한이 없습니다. 관리자에게 권한을 요청하세요.' : e.message))
    portal.get('/api/fund-plan/map').then(setMaps).catch(() => setMaps([]))
  }
  useEffect(() => { load() }, [year])

  const saveMap = async (rows) => {
    try {
      await portal.post('/api/fund-plan/map', { rows })
      notify('분류 규칙을 저장했습니다')
      load()
    } catch (e) { setErr(e.message) }
  }

  const items = data?.items ?? []
  const 수입 = items.filter((i) => i['구분'] === '수입')
  const 지출 = items.filter((i) => i['구분'] === '지출')
  const 기준 = items.find((i) => i['구분'] === '기준')

  const planOf = (id, m) => {
    const k = `${id}:${m}`
    if (k in edits) return edits[k]
    return data?.plan?.[String(id)]?.[m] ?? 0
  }
  const actualOf = (side, id, m) => data?.actual?.[side]?.[String(id ?? 'null')]?.[m] ?? 0

  const cellValue = (side, id, m) => {
    if (mode === 'plan') return planOf(id, m)
    if (mode === 'actual') return actualOf(side, id, m)
    // 차이 — 수입은 많을수록, 지출은 적을수록 좋다. 부호를 그대로 두고 색으로 구분한다.
    return actualOf(side, id, m) - planOf(id, m)
  }

  const rowSum = (side, id) => MONTHS.reduce((s, m) => s + cellValue(side, id, m), 0)
  const colSum = (side, list, m) => list.reduce((s, i) => s + cellValue(side, i.id, m), 0)

  // 미지정은 항목이 아니라 "아직 계정과목이 안 붙은 거래"다. 계획은 없고 실적만 있다.
  const 미지정 = (side, m) => (mode === 'plan' ? 0 : actualOf(side, null, m))
  const 미지정합 = (side) => MONTHS.reduce((s, m) => s + 미지정(side, m), 0)

  const 수입계 = (m) => colSum('수입', 수입, m) + 미지정('수입', m)
  const 지출계 = (m) => colSum('지출', 지출, m) + 미지정('지출', m)

  // '한눈에'는 한 칸에 계획과 실적을 같이 쓴다. cellValue는 모드를 따라가므로 쓸 수 없다.
  const 계획계 = (list, m) => list.reduce((s, i) => s + planOf(i.id, m), 0)
  const 실적계 = (side, list, m) =>
    list.reduce((s, i) => s + actualOf(side, i.id, m), 0) + actualOf(side, null, m)
  const 계획순 = (m) => 계획계(수입, m) - 계획계(지출, m)
  const 실적순 = (m) => 실적계('수입', 수입, m) - 실적계('지출', 지출, m)

  // 잔액. 계획은 연초 기준잔액에서 계획 수입·지출을 누적해 만들고, 실적은 그 달 말
  // 통장에 실제로 남아 있던 돈을 그대로 쓴다. 차이는 그 둘의 차다.
  const 실적월말 = (m) => data?.actual?.['월말잔액']?.[String(m)]
  // colSum을 쓰면 안 된다 - 차이 모드에서 cellValue가 (실적-계획)을 돌려주므로 계획잔액이
  // 차이의 누적이 되고, 그 값을 다시 실적잔액에서 빼면 아무 뜻도 없는 숫자가 나온다.
  // 7월 잔액 차이가 376억으로 찍힌 게 이것 때문이었다. 계획잔액은 어느 모드에서든 계획이다.
  const 계획월말 = (m) => {
    let v = 기준 ? planOf(기준.id, 1) : 0
    for (let k = 1; k <= m; k++) {
      v += 수입.reduce((s, i) => s + planOf(i.id, k), 0)
      v -= 지출.reduce((s, i) => s + planOf(i.id, k), 0)
    }
    return v
  }
  const 월말 = (m) => {
    if (mode === 'plan') return 계획월말(m)
    const a = 실적월말(m)
    if (a === undefined) return null
    return mode === 'actual' ? a : a - 계획월말(m)
  }
  const 월초 = (m) => (m === 1
    ? (mode === 'plan' ? (기준 ? planOf(기준.id, 1) : 0) : null)
    : 월말(m - 1))

  const save = async () => {
    const cells = Object.entries(edits).map(([k, v]) => {
      const [itemId, month] = k.split(':').map(Number)
      return { itemId, month, amount: v }
    })
    if (!cells.length) return
    setBusy(true)
    try {
      await portal.post('/api/fund-plan', { year, cells })
      notify(`${cells.length}칸 저장`)
      load()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const show = (v) => {
    if (!v) return mode === 'plan' ? '' : '·'
    return unit === '억' ? 억(v) : fmt(Math.round(v))
  }

  // 입력칸: 치는 동안의 글자는 drafts가 들고, 벗어나면 버린다. 안 그러면 억 단위에서
  // "21."의 점이 매번 지워져 소수점을 칠 수 없다.
  const 칸글자 = (k, v) => {
    if (k in drafts) return drafts[k]
    if (!v) return ''
    return unit === '억' ? String(Number((v / 1e8).toFixed(1))) : String(Math.round(v))
  }
  const 칸입력 = (k, s, id) => {
    setDrafts((p) => ({ ...p, [k]: s }))
    const raw = s.replace(/,/g, '').trim()
    if (raw === '') { setEdits((p) => ({ ...p, [k]: 0 })); return }
    const n = Number(raw)
    if (!Number.isNaN(n) && id) setEdits((p) => ({ ...p, [k]: unit === '억' ? n * 1e8 : n }))
  }
  const 칸나감 = (k) => setDrafts((p) => {
    const q = { ...p }
    delete q[k]
    return q
  })

  // 계획 / 실적 / 차이를 위에서부터 쌓는다. 값이 없는 자리는 · 로 둔다 - 0과 구분해야
  // "계획이 없다"와 "계획이 0이다"를 헷갈리지 않는다.
  const Tri = ({ p, a, side, onClick, title, sum }) => {
    const d = (p == null || a == null) ? null : a - p
    const 나쁨 = d ? (side === '지출' ? d > 0 : d < 0) : false
    return (
      <td className={'fp-cell fp-tri' + (sum ? ' fp-sum' : '') + (onClick ? ' fp-click' : '')}
        onClick={onClick} title={title}>
        <span className="t-p">{p == null ? '·' : (p ? show(p) : '·')}</span>
        <span className="t-a">{a == null ? '·' : (a ? show(a) : '·')}</span>
        <span className={'t-d' + (d ? (나쁨 ? ' fp-bad' : ' fp-good') : '')}>
          {d ? show(d) : ''}
        </span>
      </td>
    )
  }

  const Cell = ({ side, id, m }) => {
    if (mode === 'all') {
      return <Tri p={planOf(id, m)} a={actualOf(side, id, m)} side={side}
        title="누르면 이 칸에 들어간 거래를 봅니다"
        onClick={() => openDetail(side, id, m)} />
    }
    if (mode === 'plan') {
      const k = `${id}:${m}`
      const v = planOf(id, m)
      return (
        <td className={'fp-cell' + (k in edits ? ' fp-dirty' : '')}>
          <input value={칸글자(k, v)} inputMode="decimal" placeholder="·"
            onChange={(e) => 칸입력(k, e.target.value, id)}
            onBlur={() => 칸나감(k)} />
        </td>
      )
    }
    const v = cellValue(side, id, m)
    const 나쁨 = mode === 'diff' && (side === '수입' ? v < 0 : v > 0)
    return (
      <td className={'fp-cell fp-ro fp-click' + (mode === 'diff' && v ? (나쁨 ? ' fp-bad' : ' fp-good') : '')}
        title="누르면 이 칸에 들어간 거래를 봅니다"
        onClick={() => openDetail(side, id, m)}>{show(v)}</td>
    )
  }

  const Block = ({ side, list }) => (
    <>
      <tr className="fp-band"><td colSpan={14}>{side}</td></tr>
      {list.map((i) => (
        <tr key={i.id}>
          <td className="l">{i['항목명']}</td>
          {MONTHS.map((m) => <Cell key={m} side={side} id={i.id} m={m} />)}
          {mode === 'all'
            ? <Tri sum side={side}
                p={MONTHS.reduce((s, m) => s + planOf(i.id, m), 0)}
                a={MONTHS.reduce((s, m) => s + actualOf(side, i.id, m), 0)} />
            : <td className="fp-cell fp-sum">{show(rowSum(side, i.id))}</td>}
        </tr>
      ))}
      {mode !== 'plan' && (
        <tr className="fp-unmapped">
          <td className="l">미지정 <span className="muted" style={{ fontSize: 11 }}>계정과목 미지정</span></td>
          {MONTHS.map((m) => (mode === 'all'
            ? <Tri key={m} p={null} a={미지정(side, m)} side={side}
                title="누르면 아직 분류 안 된 거래를 봅니다"
                onClick={() => openDetail(side, null, m, '미지정')} />
            : <td key={m} className="fp-cell fp-ro fp-click"
                title="누르면 아직 분류 안 된 거래를 봅니다"
                onClick={() => openDetail(side, null, m, '미지정')}>{show(미지정(side, m))}</td>
          ))}
          {mode === 'all'
            ? <Tri p={null} a={미지정합(side)} side={side} sum
                onClick={() => openDetail(side, null, 0, '미지정')} />
            : <td className="fp-cell fp-sum fp-click"
                onClick={() => openDetail(side, null, 0, '미지정')}>{show(미지정합(side))}</td>}
        </tr>
      )}
      <tr className="fp-total">
        <td className="l">{side} 계</td>
        {MONTHS.map((m) => (mode === 'all'
          ? <Tri key={m} side={side}
              p={계획계(side === '수입' ? 수입 : 지출, m)}
              a={실적계(side, side === '수입' ? 수입 : 지출, m)} />
          : <td key={m} className="fp-cell">{show(side === '수입' ? 수입계(m) : 지출계(m))}</td>
        ))}
        {mode === 'all'
          ? <Tri sum side={side}
              p={MONTHS.reduce((s, m) => s + 계획계(side === '수입' ? 수입 : 지출, m), 0)}
              a={MONTHS.reduce((s, m) => s + 실적계(side, side === '수입' ? 수입 : 지출, m), 0)} />
          : <td className="fp-cell fp-sum">
              {show(MONTHS.reduce((s, m) => s + (side === '수입' ? 수입계(m) : 지출계(m)), 0))}
            </td>}
      </tr>
    </>
  )

  return (
    <>
      <div style={{ position: 'relative' }} className="no-print">
        <h2>{defaultMode === 'actual' ? '계획 대비 실적' : '자금계획'}</h2>
        <NoteDrawer target={defaultMode === 'actual' ? 'fund-plan-actual' : 'fund-plan'}
          screenKey="fund_plan" context={{ year }} contextLabel={String(year)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>

      <div className="row no-print" style={{ marginBottom: 12 }}>
        <span className="muted">연도</span>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[year + 1, year, year - 1, year - 2].filter((v, i, a) => a.indexOf(v) === i)
            .sort((a, b) => b - a).map((y) => <option key={y} value={y}>{y}년</option>)}
        </select>
        <span className="seg">
          {MODES.map((m) => (
            <label key={m.k} className={mode === m.k ? 'on' : ''}>
              <input type="radio" name="fpmode" checked={mode === m.k}
                onChange={() => setMode(m.k)} />{m.label}
            </label>
          ))}
        </span>
        <select value={unit} onChange={(e) => setUnit(e.target.value)}>
          <option value="억">억원</option>
          <option value="원">원</option>
        </select>
        {mode === 'plan' && (
          <button className="btn" onClick={save} disabled={busy || !Object.keys(edits).length}>
            {busy ? '저장 중…' : `계획 저장 (${Object.keys(edits).length})`}
          </button>
        )}
        {data?.actual?.['출처'] && (
          <span className="muted" style={{ fontSize: 12 }}
            title="계좌로 가린 것이 가장 정확하고, 자동추정은 규칙으로 붙인 계정과목이라 자주 틀립니다.">
            실적 {fmt(data.actual['건수'])}건 ·{' '}
            {['계좌', '부서', '거래처', '사람지정', '미지정']
              .map((k) => `${k} ${(data.actual['출처'][k] / 1e8).toFixed(1)}억`).join(' · ')}
          </span>
        )}
      </div>

      <div className="hint no-print" style={{ marginBottom: 12 }}>
        {mode === 'all'
          ? '칸마다 위에서부터 계획 · 실적 · 차이입니다. 계획 입력은 계획 탭에서 합니다.'
          : mode === 'map'
          ? '실적을 어느 항목에 붙일지 정합니다.'
          : mode === 'plan'
          ? '계획만 입력하시면 됩니다. 실적은 은행 거래에서 자동으로 모읍니다 — 따로 넣지 않습니다.'
          : mode === 'actual'
            ? '그 달 통장에 실제로 들어오고 나간 현금입니다. 계좌끼리 옮긴 돈은 뺐습니다.'
            : '실적 − 계획. 수입은 모자라면, 지출은 넘치면 빨간색입니다.'}
      </div>

      {err && <div className="error-box">{err}</div>}
      {!err && !data && <div className="muted">불러오는 중…</div>}

      {data && mode === 'map' && (
        <div className="panel" style={{ padding: 16 }}>
          <div className="hint" style={{ marginBottom: 12 }}>
            실적을 어느 항목에 붙일지 정하는 규칙입니다. 키는 조각만 적어도 됩니다(“링크맘”이
            “링크맘사업부_MD”에 걸립니다).
            <br />
            <b>구체적인 지정이 뭉뚱그린 규칙을 이깁니다 — 부서 › 계정과목 › 거래처 › 계좌 순으로 봅니다.</b>
            그래서 한 거래처 안에서 항목이 갈릴 때는, 기본을 거래처로 걸어 두고 예외인 거래에만
            계정과목이나 부서를 지정하면 됩니다 — 「거래처 꿈비 → 구매」를 걸어 두고 수수료 건에만
            계정과목 「지급수수료」를 지정하면 그 건은 경비로 갑니다.
            <br />
            방향은 고른 항목이 수입이냐 지출이냐로 정해지므로,
            <b>쿠팡처럼 팔기도 하고 사기도 하는 거래처는 두 줄로 겁니다</b> —
            쿠팡→온라인(수입), 쿠팡→경비(지출).
          </div>
          <div className="row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
            <select value={draft.종류} onChange={(e) => setDraft({ ...draft, 종류: e.target.value })}>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            {draft.종류 === '계좌' && (
              <select value={draft.키} style={{ maxWidth: 300 }}
                onChange={(e) => setDraft({ ...draft, 키: e.target.value })}>
                <option value="">계좌 선택</option>
                {accts.map((a) => (
                  <option key={a.assetId} value={a.assetId}>
                    {a.shortLabel}{a.alias || a.nickname ? ` · ${a.alias || a.nickname}` : ''}
                  </option>
                ))}
              </select>
            )}
            {draft.종류 === '부서' && (
              <select value={draft.키} style={{ maxWidth: 260 }}
                onChange={(e) => setDraft({ ...draft, 키: e.target.value })}>
                <option value="">부서 선택</option>
                {depts.map((d) => <option key={d.code} value={d.name}>{d.buName} · {d.name}</option>)}
              </select>
            )}
            {draft.종류 === '계정과목' && (
              <select value={draft.키} style={{ maxWidth: 260 }}
                onChange={(e) => setDraft({ ...draft, 키: e.target.value })}>
                <option value="">계정과목 선택</option>
                {cats.map((x) => <option key={x.code} value={x.name}>{x.name}</option>)}
              </select>
            )}
            {draft.종류 === '거래처' && (
              <span className="vendor-edit">
                <input placeholder="거래처 이름 (일부만 쳐도 됩니다)" value={draft.키}
                  style={{ width: 240 }}
                  onChange={(e) => setDraft({ ...draft, 키: e.target.value })} />
                {vsug.length > 0 && (
                  <span className="vendor-sugs">
                    {vsug.slice(0, 8).map((v) => (
                      <button key={v.code} type="button" onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setDraft({ ...draft, 키: v.name }); setVsug([]) }}>
                        {v.name} <i>{v.code}</i>
                      </button>
                    ))}
                  </span>
                )}
              </span>
            )}
            <select value={draft.item_id} onChange={(e) => setDraft({ ...draft, item_id: e.target.value })}>
              <option value="">항목 선택</option>
              <optgroup label="수입">
                {수입.map((i) => <option key={i.id} value={i.id}>{i['항목명']}</option>)}
              </optgroup>
              <optgroup label="지출">
                {지출.map((i) => <option key={i.id} value={i.id}>{i['항목명']}</option>)}
              </optgroup>
            </select>
            <button className="btn" disabled={!draft.키.trim() || !draft.item_id}
              onClick={() => {
                saveMap([{ 종류: draft.종류, 키: draft.키.trim(), item_id: Number(draft.item_id) }])
                setDraft({ ...draft, 키: '', item_id: '' })
              }}>규칙 추가</button>
            <span className="muted" style={{ fontSize: 12 }}>{fmt(maps.length)}개</span>
          </div>
          <table>
            <thead><tr>
              <th style={{ width: 90 }}>종류</th><th className="l">키</th>
              <th style={{ width: 70 }}>방향</th><th className="l" style={{ width: 170 }}>항목</th>
              <th style={{ width: 130 }}>{year}년 적중</th>
              <th style={{ width: 60 }}></th>
            </tr></thead>
            <tbody>
              {maps.length === 0 && <tr><td colSpan={6} className="c muted">규칙이 없습니다</td></tr>}
              {maps.map((m) => {
                const it = items.find((x) => x.id === m.item_id)
                const hit = data?.actual?.['적중']?.[`${m['종류']}|${m['키']}|${m['구분']}`]
                const acct = m['종류'] === '계좌' ? accts.find((a) => String(a.assetId) === String(m['키'])) : null
                return (
                  <tr key={`${m['종류']}|${m['키']}|${m['구분']}`}>
                    <td className="c">{m['종류']}</td>
                    <td className="l">{acct ? `${acct.shortLabel} · ${acct.alias || acct.nickname || ''}` : m['키']}</td>
                    <td className="c"><span className={m['구분'] === '수입' ? 'ok' : 'muted'}>{m['구분']}</span></td>
                    <td className="l">{it ? it['항목명'] : '(삭제된 항목)'}</td>
                    <td className="c">
                      {hit
                        ? <span>{fmt(hit['건수'])}건 · {억(hit['금액'])}억</span>
                        : <span className="warn" title="이 규칙에 걸리는 거래가 없습니다. 키를 확인해 주세요.">0건</span>}
                    </td>
                    <td className="c">
                      <button type="button" className="btn danger"
                        onClick={() => saveMap([{ ...m, deleted: true }])}>삭제</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && mode !== 'map' && (
        <div className="panel" style={{ overflowX: 'auto' }}>
          <table className="fp">
            <thead>
              <tr>
                <th className="l" style={{ minWidth: 128 }}>
                  항목
                  {mode === 'all' && (
                    <div className="muted" style={{ fontSize: 10, fontWeight: 400, marginTop: 2 }}>
                      계획 / 실적 / 차이
                    </div>
                  )}
                </th>
                {MONTHS.map((m) => <th key={m}>{m}월</th>)}
                <th>연간</th>
              </tr>
            </thead>
            <tbody>
              <tr className="fp-band"><td colSpan={14}>잔액</td></tr>
              <tr>
                <td className="l">월초잔액</td>
                {MONTHS.map((m) => {
                  const v = 월초(m)
                  if (mode === 'plan' && m === 1) {
                    const k = `${기준?.id}:1`
                    return (
                      <td key={m} className={'fp-cell' + (k in edits ? ' fp-dirty' : '')}>
                        <input value={칸글자(k, v)} placeholder="기준잔액" inputMode="decimal"
                          title="연초 기준잔액입니다. 이후 달은 앞달 월말잔액을 이어받습니다."
                          onChange={(e) => 칸입력(k, e.target.value, 기준?.id)}
                          onBlur={() => 칸나감(k)} />
                      </td>
                    )
                  }
                  if (mode === 'all') {
                    return <Tri key={m} p={m === 1 ? planOf(기준?.id, 1) : 계획월말(m - 1)}
                      a={m === 1 ? null : (실적월말(m - 1) ?? null)} />
                  }
                  return <td key={m} className="fp-cell fp-ro">{v === null ? '·' : show(v)}</td>
                })}
                <td className="fp-cell fp-sum"></td>
              </tr>
              <Block side="수입" list={수입} />
              <Block side="지출" list={지출} />
              <tr className="fp-net">
                <td className="l">순증감</td>
                {MONTHS.map((m) => (mode === 'all'
                  ? <Tri key={m} p={계획순(m)} a={실적순(m)} />
                  : (() => {
                    const v = 수입계(m) - 지출계(m)
                    return <td key={m} className={'fp-cell' + (v < 0 ? ' fp-bad' : '')}>{show(v)}</td>
                  })()
                ))}
                {mode === 'all'
                  ? <Tri sum
                      p={MONTHS.reduce((s, m) => s + 계획순(m), 0)}
                      a={MONTHS.reduce((s, m) => s + 실적순(m), 0)} />
                  : <td className="fp-cell fp-sum">
                      {show(MONTHS.reduce((s, m) => s + 수입계(m) - 지출계(m), 0))}
                    </td>}
              </tr>
              <tr className="fp-net">
                <td className="l">월말잔액</td>
                {MONTHS.map((m) => (mode === 'all'
                  ? <Tri key={m} p={계획월말(m)} a={실적월말(m) ?? null} />
                  : (() => {
                    const v = 월말(m)
                    return <td key={m} className={'fp-cell' + (v !== null && v < 0 ? ' fp-bad' : '')}>
                      {v === null ? '·' : show(v)}</td>
                  })()
                ))}
                <td className="fp-cell fp-sum"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {detail && (
        <div className="modal-backdrop" onMouseDown={() => setDetail(null)}>
          <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 940 }}>
            <div className="modal-header">
              <h3>
                {detail.side} · {detail.name || items.find((i) => i.id === detail.itemId)?.['항목명'] || '미지정'}
                {detail.month ? ` · ${detail.month}월` : ` · ${year}년 전체`}
              </h3>
              <button type="button" className="modal-close" onClick={() => setDetail(null)}>×</button>
            </div>
            <div style={{ padding: 18, maxHeight: '72vh', overflowY: 'auto' }}>
              {detail.loading && <div className="muted">불러오는 중…</div>}
              {detail.err && <div className="error-box">{detail.err}</div>}
              {!detail.loading && !detail.err && (
                <>
                  <div className="hint" style={{ marginBottom: 12 }}>
                    {fmt(detail['건수'])}건. <b>규칙은 이름 단위로 겁니다</b> — 아래 이름별 합계에서
                    큰 것부터 「분류 규칙」 탭에 넣으면 한 번에 잡힙니다.
                    거래처가 비어 있으면 데이터 화면에서 거래처를 먼저 매칭해야 합니다.
                  </div>
                  <b>이름별 합계</b>
                  <table style={{ marginBottom: 18 }}>
                    <thead><tr>
                      <th className="l">이름</th><th style={{ width: 90 }}>거래처매칭</th>
                      <th style={{ width: 80 }}>건수</th><th style={{ width: 120 }}>금액</th>
                    </tr></thead>
                    <tbody>
                      {(detail['이름별'] || []).map((g) => (
                        <tr key={g['이름']}>
                          <td className="l">{g['이름']}</td>
                          <td className="c">
                            {g['거래처매칭']
                              ? <span className="ok">매칭됨</span>
                              : <span className="warn" title="위하고 거래처가 안 붙어 있어 거래처 규칙으로 못 겁니다">미매칭</span>}
                          </td>
                          <td className="fp-cell">{fmt(g['건수'])}</td>
                          <td className="fp-cell">{fmt(Math.round(g['금액']))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <b>거래 내역 {detail['건수'] > 300 ? '(금액 큰 300건)' : ''}</b>
                  <table>
                    <thead><tr>
                      <th style={{ width: 88 }}>일자</th><th className="l">계좌</th>
                      <th className="l">상대방</th><th className="l">거래처</th>
                      <th className="l">부서</th><th className="l">계정과목</th>
                      <th style={{ width: 120 }}>금액</th>
                    </tr></thead>
                    <tbody>
                      {(detail.rows || []).map((r, i) => (
                        <tr key={i}>
                          <td className="c">{r['일자']}</td>
                          <td className="l">{r['계좌']}</td>
                          <td className="l">{r['상대방']}</td>
                          <td className="l">{r['거래처'] || <span className="muted">—</span>}</td>
                          <td className="l">{r['부서'] || <span className="muted">—</span>}</td>
                          <td className="l">{r['계정과목'] || <span className="muted">—</span>}</td>
                          <td className="fp-cell">{fmt(Math.round(r['금액']))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
